import { Suspense } from "react";
import { useLocation } from "@tanstack/react-router";
import { PageSkeleton } from "@/ui/page";
import { boardsAtPath } from "./registry";
import type { BoardDef } from "./types";

/**
 * Placeholder routing for routes that several boards share (states / windows of one screen): picks the board
 * whose `href` matches the current URL best — exact path + search, then same search keys, then the first board
 * of the route. Real screens replace `component: boardsAt(...)` with data-driven selection
 * (dossier status → drawer step, `?fenetre=` → window…).
 */
export function pickBoard(boards: BoardDef[], pathname: string, search: string): BoardDef | undefined {
  const here = new URLSearchParams(search);
  const score = (b: BoardDef) => {
    const u = new URL(b.href, "http://x");
    let s = 0;
    if (u.pathname === pathname) s += 100;
    const want = new URLSearchParams(u.search);
    for (const [k, v] of want) s += here.get(k) === v ? 10 : -10;
    if ([...want].length === 0 && [...here].length === 0) s += 1;
    return s;
  };
  return [...boards].sort((a, b) => score(b) - score(a))[0];
}

export function boardsAt(path: string) {
  const boards = boardsAtPath(path);
  return function BoardRoute() {
    const loc = useLocation();
    const board = pickBoard(boards, loc.pathname, loc.searchStr);
    if (!board) return null;
    const C = board.component;
    return (
      <Suspense fallback={board.kind === "auth" ? null : <PageSkeleton />}>
        <C />
      </Suspense>
    );
  };
}
